import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ensureNamespaceLoaded } from "../i18n/lazyNamespaces";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Paper,
  Divider,
  useTheme,
} from "@mui/material";
import SetupStepHelp from "./setup/SetupStepHelp";

const EtapaResumoFinal = ({
  companyName,
  selectedAwarenessTitles,
  manualClients,
  manualProducts,
  pipelineSteps,
  requestTypes,
  channelsLines,
  pesquisasConfiguradas,
  cargosConfigurados,
  usuariosCadastrados,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("onboarding");
  useEffect(() => {
    void ensureNamespaceLoaded("onboarding");
  }, []);

  const formattedRequestTypes =
    Array.isArray(requestTypes) && requestTypes.length
      ? requestTypes
          .map((rt) => {
            const tipo = rt?.tipo || "";
            const subtipos = Array.isArray(rt?.subtipos)
              ? rt.subtipos.filter((s) => s && String(s).trim())
              : [];
            if (!tipo) return null;
            if (!subtipos.length) return tipo;
            return `${tipo}: ${subtipos.join(", ")}`;
          })
          .filter(Boolean)
          .join(" | ")
      : "";

  const channelsSecondary =
    Array.isArray(channelsLines) && channelsLines.length
      ? channelsLines.join(", ")
      : t("setup.summary.noChannels");

  return (
    <Box sx={{ width: "100%"}}>
      <SetupStepHelp stepKey="summary" />
      <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: theme.palette.success.main }}>
        {t("setup.summary.title")}
      </Typography>

      <Typography variant="body1" gutterBottom>
        {t("setup.summary.intro")}
      </Typography>

      <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3 }}>
        {t("setup.summary.configuredHeading")}
      </Typography>

      <Paper variant="outlined" sx={{ mt: 2 }}>
        <List>
          <ListItem>
            <ListItemText
              primary={t("setup.summary.rows.company")}
              secondary={companyName || t("setup.summary.notProvided")}
            />
          </ListItem>

          <Divider />

          <ListItem>
            <ListItemText
              primary={t("setup.summary.rows.awareness")}
              secondary={
                selectedAwarenessTitles?.join(", ") ||
                t("setup.summary.noAwareness")
              }
            />
          </ListItem>

          <Divider />

          <ListItem>
            <ListItemText
              primary={t("setup.summary.rows.clients")}
              secondary={
                manualClients.length > 0
                  ? t("setup.summary.clientsCount", { count: manualClients.length })
                  : t("setup.summary.noClients")
              }
            />
          </ListItem>

          <Divider />

          <ListItem>
            <ListItemText
              primary={t("setup.summary.rows.products")}
              secondary={
                manualProducts.length > 0
                  ? t("setup.summary.productsCount", { count: manualProducts.length })
                  : t("setup.summary.noProducts")
              }
            />
          </ListItem>

          <Divider />

          <ListItem>
            <ListItemText
              primary={t("setup.summary.rows.pipeline")}
              secondary={
                pipelineSteps.length > 0
                  ? [pipelineSteps[0].from, ...pipelineSteps.map((p) => p.to)].join(" → ")
                  : t("setup.summary.noPipeline")
              }
            />
          </ListItem>

          <Divider />

          <ListItem>
            <ListItemText
              primary={t("setup.summary.rows.requestTypes")}
              secondary={formattedRequestTypes || t("setup.summary.noRequestTypes")}
            />
          </ListItem>

          <Divider />

          <ListItem>
            <ListItemText
              primary={t("setup.summary.rows.channels")}
              secondary={channelsSecondary}
            />
          </ListItem>

          <Divider />

          <ListItem>
            <ListItemText
              primary={t("setup.summary.rows.roles")}
              secondary={
                cargosConfigurados.length > 0
                  ? t("setup.summary.rolesCount", { count: cargosConfigurados.length })
                  : t("setup.summary.noRoles")
              }
            />
          </ListItem>

          <Divider />

          <ListItem>
            <ListItemText
              primary={t("setup.summary.rows.users")}
              secondary={
                usuariosCadastrados.length > 0
                  ? t("setup.summary.usersCount", { count: usuariosCadastrados.length })
                  : t("setup.summary.noUsers")
              }
            />
          </ListItem>
        </List>
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }} >
        {t("setup.summary.footer")}
      </Typography>
    </Box>
  );
};

export default EtapaResumoFinal;
